import './styles.scss'

import I18n from 'lib/i18n'
import React from 'react'
import { useSelector } from 'react-redux'
import { useActions, useValues } from 'kea'
import PropTypes from 'prop-types'

import Twicpics from 'lib/tags/twicpics'
import displayProfileName from 'lib/utils/display-profile-name'
import Reputation from '~/scenes/search/search-page/results/reputation'

import Lightbox from './lightbox/index'
import lightBoxLogic from './lightbox/logic'
import FavoriteButton from './favorite-button/index'
import EditButton from './edit-button'

import railsContext from 'lib/selectors/rails-context'
import VideoPlayer from '~/scenes/shared/video-player'
import ReviewLine from '~/scenes/shared/review-line'

const TARGETS_SECTION = 'reviews'

export default function ProfileHeader ({ course, user, video, avatar }) {
  const { openVideo, closeVideo, setCurrentImage } = useActions(lightBoxLogic({ user }))
  const { isVisible } = useValues(lightBoxLogic)
  const {
    id,
    firstName,
    url,
    isMe,
    avatarAlt: userAvatarAlt,
    avatarUrl,
    isVerified,
    editUrl,
    photos,
    reviewCount,
    avgReview100,
    isPremium,
    locationString
  } = user
  const { avatarAlt: courseAvatarAlt } = course || {}
  const { dpr, currentUserId } = useSelector(railsContext)

  const lookingAtMyself = currentUserId && currentUserId === id
  const profileName = displayProfileName(firstName)
  const reviewText = I18n.t('profiles.profile_layout.review_count', { count: reviewCount })

  return (
    <div className='react-profile-header'>
      <VideoPlayer isVisible={isVisible} url={video.youtubeEmbedUrl} title={video.title} closeVideo={closeVideo} />
      <div className='upto-659'>
        <div className='widget result profile-widget-mobile'>
          <div
            className='fancybox profile-image-container'
            onClick={video.state === 'listed' ? openVideo : () => setCurrentImage(0)}
          >
            {video.state === 'listed' && (
              <div className='play-btn-container-mobile'>
                <div className='play-btn-mobile' />
              </div>
            )}

            <Twicpics src={avatar || avatarUrl} width={100} alt={courseAvatarAlt || userAvatarAlt} dpr={dpr} lazyLoad />
            {isVerified && <Reputation id={id} className='verified-badge verified-badge-small' />}
          </div>

          <ul className='attributes'>
            {reviewCount > 0 && (
              <li className='rating'>
                <a className='rate-stars' href='#reviews' aria-label={courseAvatarAlt || userAvatarAlt}>
                  <div className='rate-1' style={{ width: avgReview100 + '%' }} />
                </a>
              </li>
            )}
          </ul>

          <h5 className='mobile-name'>
            <a href={url}>{profileName}</a>
          </h5>

          {locationString && (
            <div className='block location-mobile'>
              <div className='block-row'>
                <div className='element icon-element'>
                  <img
                    className='home-icon lazyload'
                    alt='home icon'
                    data-src='/assets/icons/contact_home.png'
                    width={20}
                    height={20}
                  />
                </div>
                <div className='element location-text'>
                  {locationString.map(chunk => (
                    <span key={chunk.i}>
                      <a href={chunk.url}>{chunk.title}</a>
                      {!chunk.last && (
                        <span className='breadcrumb-child'>
                          <span className='main' />
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isPremium && (
            <div className='block premium-mobile'>
              <div className='block-row' title={I18n.t('profiles.profile_layout.premium_hint')}>
                <div className='element icon-element'>
                  <img
                    className='member-icon lazyload'
                    alt='member icon'
                    data-src='/assets/icons/rep_member.png'
                    width={20}
                    height={20}
                  />
                </div>
                <div className='element premium-text'>{I18n.t('profiles.pro_badge')}</div>
              </div>
            </div>
          )}

          <div className='clearfix' />

          {photos && photos.length > 0 && (
            <div className='photos mobile-photos'>
              {photos.slice(0, 10).map(photo => (
                <Twicpics
                  key={photo.id}
                  alt={photo.title || ''}
                  src={photo.url}
                  onClick={() => setCurrentImage(photo.i)}
                  width={50}
                  dpr={dpr}
                  lazyLoad
                />
              ))}
            </div>
          )}
        </div>

        <div className='action-mobile'>
          {!lookingAtMyself ? (
            <div>{course && <FavoriteButton />}</div>
          ) : (
            <div>{!course && <EditButton profileEditPath={editUrl} />}</div>
          )}
        </div>
      </div>

      <div className='over-659 profilebox'>
        <div className='profilename'>
          <h5>
            <a href={url}>{profileName}</a>
          </h5>
        </div>
        <div className='widget space clearfix profile-widget-desktop'>
          <div className='profile-cell1'>
            <div className='image'>
              <div className='fancybox' onClick={video.state === 'listed' ? openVideo : () => setCurrentImage(0)}>
                {video.state === 'listed' && (
                  <div className='play-btn-container'>
                    <div className='play-btn' />
                  </div>
                )}
                <Twicpics
                  src={avatar || avatarUrl}
                  width={200}
                  alt={courseAvatarAlt || userAvatarAlt}
                  dpr={dpr}
                  lazyLoad
                />
                {isVerified && <Reputation id={id} className='verified-badge verified-badge-full' />}
              </div>

              {isMe && !video.state && (
                <>
                  <a className='edit-bg' href={editUrl}>
                    &nbsp;
                  </a>
                  <a className='edit-text' href={editUrl}>
                    {I18n.t('profiles.profile_layout.change_photo')}
                  </a>
                </>
              )}
            </div>
            <div className='profile-afterimg'>
              {photos && photos.length > 0 && (
                <div className='photos'>
                  {photos.slice(0, 4).map(photo => (
                    <Twicpics
                      key={photo.id}
                      alt={photo.title || ''}
                      src={photo.url}
                      onClick={() => setCurrentImage(photo.i)}
                      width={50}
                      dpr={dpr}
                      lazyLoad
                    />
                  ))}
                </div>
              )}

              <Lightbox user={user} />

              {!lookingAtMyself ? (
                <div>{course && <FavoriteButton />}</div>
              ) : (
                <div>{!course && <EditButton profileEditPath={editUrl} />}</div>
              )}
            </div>
          </div>

          <div className='profile-cell2'>
            <div className='firstrow'>
              {reviewCount > 0 && (
                <div className='block review-desktop'>
                  <div className='block-row'>
                    <div className='element icon-element'>
                      <img
                        className='rating-icon lazyload'
                        alt='rating icon'
                        data-src='/assets/icons/contact_star.png'
                        width={31}
                        height={31}
                      />
                    </div>
                    <div className='element'>
                      <ReviewLine
                        avgReview100={avgReview100}
                        reviewText={reviewText}
                        reviewCount={reviewCount}
                        targetSection={TARGETS_SECTION}
                      />
                    </div>
                  </div>
                </div>
              )}
              {locationString && (
                <div className='block location-desktop'>
                  <div className='block-row'>
                    <div className='element icon-element'>
                      <img
                        className='home-icon lazyload'
                        alt='home icon'
                        data-src='/assets/icons/contact_home.png'
                        width={31}
                        height={31}
                      />
                    </div>
                    <div className='element'>
                      {locationString.map(chunk => (
                        <span key={chunk.i}>
                          <a href={chunk.url}>{chunk.title}</a>
                          {chunk.last ? null : ', '}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {isPremium && (
                <div className='block'>
                  <div className='block-row' title={I18n.t('profiles.profile_layout.premium_hint')}>
                    <div className='element'>
                      <img
                        className='member-icon lazyload'
                        alt='member icon'
                        data-src='/assets/icons/rep_member.png'
                        width={31}
                        height={31}
                      />
                    </div>
                    <div className='element'>{I18n.t('profiles.pro_badge')}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

ProfileHeader.propTypes = {
  course: PropTypes.object,
  user: PropTypes.object,
  video: PropTypes.object,
  avatar: PropTypes.string
}
